/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState, useRef } from 'react'
import cloneDeep from 'lodash/cloneDeep'
import { Formik } from 'formik'
import { pluck, findWhere, contains, reduce, chain, without } from 'underscore'
import { Select } from '~/components/Select/Select'
import { useServiceContext } from '~/root'
import { transformToSelectOptions } from '~/utils/selectOptions'
import { MasterDataType } from '~/constants/etmf'
import { getSelectionsForPackage, sortByFsiName } from '~/services/api/etmf'

interface LevelFormValues {
  product: number | string
  trial: number | string
  environment: number | string
  countries: Array<number | string>
  sites: Array<number | string>
}

interface SelectOption {
  fsiId: string | number
  fsiName: string
  parentId?: string | number
}

interface LevelSettingsDefaults {
  level: string | null
  product: SelectOption | null
  trial: SelectOption | null
  environment: SelectOption | null
  countries: SelectOption[]
  sites: SelectOption[]
}

interface LevelProps {
  packageData: any
  config: any
  options: any
  updateConfigData: (data: any) => void
  updateOptionsData: (data: any) => void
}

const Level: React.FC<LevelProps> = ({ packageData, config, options, updateConfigData, updateOptionsData }) => {
  const formRef = useRef<any>(null)
  const context = useServiceContext()
  const { translate } = context.service.filter
  const { $rootScope } = context
  const [pckgData, setPckgData] = useState<any>(null)
  const [configData, setConfigData] = useState<any>(null)
  const [optionsData, setOptionsData] = useState<any>(null)
  const [opts, setOpts] = useState<any>(null)
  const [conf, setConf] = useState<any>(null)
  const [formValues, setFormValues] = useState<LevelFormValues>({
    product: 0,
    trial: 0,
    environment: 0,
    countries: [],
    sites: []
  })
  const [initialLoad, setInitialLoad] = useState(true)
  const [settingsDefaults, setSettingsDefaults] = useState<LevelSettingsDefaults>({
    level: null,
    product: null,
    trial: null,
    environment: null,
    countries: [],
    sites: []
  })
  const [isSubmitted, setIsSubmitted] = useState(false)

  const LEVEL = {
    PRODUCT: 'PRODUCT',
    STUDY: 'STUDY',
    COUNTRY: 'COUNTRY',
    SITE: 'SITE'
  }

  const OPTIONS = {
    allCountries: {
      fsiName: translate('ETMF_LABEL_SELECT_ALL_COUNTRIES'),
      fsiId: 'ALL'
    },
    allSites: {
      fsiName: translate('ETMF_LABEL_SELECT_ALL_SITES'),
      fsiId: 'ALL'
    }
  }

  const PROPERTY = {
    level: 'level',
    product: 'product',
    trial: 'trial',
    environment: 'environment',
    countries: 'countries',
    sites: 'sites'
  }

  useEffect(() => {
    const unsubscribe = $rootScope.$on('validatePackageForms', validate)
    return () => unsubscribe()
  }, [$rootScope])

  useEffect(() => {
    if (
      pckgData &&
      Object.keys(pckgData).length &&
      configData &&
      Object.keys(configData).length &&
      optionsData &&
      Object.keys(optionsData).length &&
      initialLoad
    ) {
      const settingsData: LevelSettingsDefaults = {
        ...settingsDefaults,
        level: pckgData?.level
      }
      const configDataLocal = cloneDeep(configData)
      const optionsDataLocal = cloneDeep(optionsData)
      setSettingsDefaults(settingsData)
      if (!config?.levels) {
        configDataLocal.levels = cloneDeep(settingsData)
        setConfigData((data: any) => ({ ...data, levels: configDataLocal.levels }))
      } else {
        setFormValues((data) => ({
          ...data,
          product: config?.levels?.product?.fsiId,
          trial: config?.levels?.trial?.fsiId,
          environment: config?.levels?.environment?.fsiId,
          countries: pluck(config?.levels?.countries, 'fsiId'),
          sites: pluck(config?.levels?.sites, 'fsiId')
        }))
      }
      if (!options?.levels) {
        optionsDataLocal.levels = {}
        setOptionsData((data: any) => ({ ...data, levels: optionsDataLocal.levels }))
      }
      const optsData = optionsDataLocal.levels
      const confData = configDataLocal.levels
      setOpts(optsData)
      setConf(confData)
      loadProducts(optsData, confData)
      setInitialLoad(false)
    }
  }, [pckgData, configData, optionsData])

  useEffect(() => {
    setPckgData(packageData)
  }, [packageData])

  useEffect(() => {
    if (config) {
      setConfigData(config)
    }
  }, [config])

  useEffect(() => {
    updateConfigData(conf)
  }, [conf])

  useEffect(() => {
    updateOptionsData(opts)
  }, [opts])

  useEffect(() => {
    if (options) {
      setOptionsData(options)
    }
  }, [options])

  const validate = () => {
    setIsSubmitted(true)
    formRef.current.validateForm().then(() => {
      const errors = formRef?.current?.errors ?? {}
      $rootScope.$emit('levelValidation', Object.keys(errors).length ? false : true)
    })
  }

  const isProductEnabled = (confData: any) => {
    return confData?.level
  }

  const isStudyEnabled = (confData: any) => {
    return showStudy(confData) && isProductEnabled(confData) && confData?.product
  }

  const isEnvironmentEnabled = (confData: any) => {
    return showEnvironment(confData) && isStudyEnabled(confData) && confData?.trial
  }

  const isCountryEnabled = (confData: any) => {
    return showCountry(confData) && isEnvironmentEnabled(confData) && confData?.environment
  }

  const showStudy = (confData: any) => {
    return [LEVEL.STUDY, LEVEL.COUNTRY, LEVEL.SITE].indexOf(confData?.level) >= 0
  }

  const showEnvironment = (confData: any) => {
    return [LEVEL.STUDY, LEVEL.COUNTRY, LEVEL.SITE].indexOf(confData?.level) >= 0
  }

  const showCountry = (confData: any) => {
    return [LEVEL.COUNTRY, LEVEL.SITE].indexOf(confData?.level) >= 0
  }

  const showSite = (confData: any) => {
    return [LEVEL.SITE].indexOf(confData?.level) >= 0
  }

  const loadProducts = async (optsData = cloneDeep(opts), confData = cloneDeep(conf)) => {
    if (confData?.level) {
      if (!opts?.products) {
        const data = await getSelectionsForPackage(MasterDataType.PRODUCT, null, pckgData?.filePlanId)
        optsData.products = sortByFsiName(data)
        setOpts((data: any) => ({ ...data, products: optsData.products }))
      }

      if (isProductEnabled(confData) && optsData?.products?.length === 1) {
        confData.product = optsData.products[0]
        setConf((data: any) => ({ ...data, product: confData.product }))
        setTimeout(() => {
          formRef.current.setFieldValue('product', confData.product?.fsiId)
        }, 0)
      }

      await onProductChange(optsData, confData)
    }
  }

  const onProductChange = async (optsData = cloneDeep(opts), confData = cloneDeep(conf)) => {
    if (confData?.product?.fsiId && !findWhere(optsData?.products, { fsiId: confData?.product?.fsiId })) {
      confData.product = null
      setConf((data: any) => ({ ...data, product: null }))
    }

    if (confData?.product) {
      if (showStudy(confData)) {
        const data = await getSelectionsForPackage(MasterDataType.STUDY, confData?.product?.fsiId, pckgData?.filePlanId)
        optsData.trials = sortByFsiName(data)
        setOpts((data: any) => ({ ...data, trials: optsData.trials }))

        if (isStudyEnabled(confData) && optsData.trials?.length === 1) {
          confData.trial = optsData?.trials[0]
          setConf((data: any) => ({ ...data, trial: confData.trial }))
          setTimeout(() => {
            formRef.current.setFieldValue('trial', confData.trial?.fsiId)
          }, 0)
        }

        await onStudyChange(optsData, confData)
      }
    }
  }

  const onStudyChange = async (optsData = cloneDeep(opts), confData = cloneDeep(conf)) => {
    if (confData?.trial?.fsiId && !findWhere(optsData.trials, { fsiId: confData.trial.fsiId })) {
      confData.trial = null
      setConf((data: any) => ({ ...data, trial: null }))
    }
    if (confData.trial) {
      if (showEnvironment(confData)) {
        const data = await getSelectionsForPackage(
          MasterDataType.ENVIRONMENT,
          confData?.trial?.fsiId,
          pckgData.filePlanId
        )
        optsData.environments = data
        setOpts((data: any) => ({ ...data, environments: optsData.environments }))

        if (isEnvironmentEnabled(confData) && optsData?.environments?.length === 1) {
          confData.environment = optsData.environments[0]
          setConf((data: any) => ({ ...data, environment: confData.environment }))
          setTimeout(() => {
            formRef.current.setFieldValue('environment', confData.environment?.fsiId)
          }, 0)
        }

        await onEnvironmentChange(optsData, confData)
      }
    }
  }

  const onEnvironmentChange = async (optsData = cloneDeep(opts), confData = cloneDeep(conf)) => {
    if (confData?.environment?.fsiId && !findWhere(optsData.environments, { fsiId: confData.environment.fsiId })) {
      confData.environment = null
      setConf((data: any) => ({ ...data, environment: null }))
    }

    if (confData?.environment) {
      if (showCountry(confData)) {
        const data = await getSelectionsForPackage(
          MasterDataType.COUNTRY,
          confData?.environment?.fsiId,
          pckgData.filePlanId
        )
        optsData.countries = data ? data : []
        setOpts((data: any) => ({ ...data, countries: optsData.countries }))

        if (isCountryEnabled(confData) && optsData?.countries?.length === 1) {
          confData.countries = [optsData.countries[0]]
          setConf((data: any) => ({ ...data, countries: confData.countries }))
          setTimeout(() => {
            formRef.current.setFieldValue('countries', [confData.countries[0].fsiId])
          }, 0)
        }

        await onCountryChange(optsData, confData)
      }
    }
  }

  const onCountryChange = async (optsData = cloneDeep(opts), confData = cloneDeep(conf)) => {
    // ensure options are valid
    optsData.countries = reduce(
      optsData.countries,
      (countries: SelectOption[], country: SelectOption) => {
        if (
          country.fsiName !== 'ALL' &&
          country.fsiName !== OPTIONS.allCountries.fsiName &&
          country.fsiId !== OPTIONS.allCountries.fsiId
        ) {
          countries.push(country)
        }
        return countries
      },
      []
    )

    if (optsData?.countries?.length > 1) {
      optsData.countries.push(OPTIONS.allCountries)
    }

    setOpts((data: any) => ({ ...data, countries: optsData.countries }))

    // ensure selection is in options
    confData.countries = reduce(
      confData.countries,
      (countries: SelectOption[], country: SelectOption) => {
        const countryFound = findWhere(optsData.countries, { fsiId: country.fsiId })
        if (countryFound) {
          countries.push(countryFound)
        }
        return countries
      },
      []
    )

    // if all is selected
    if (contains(confData.countries, OPTIONS.allCountries)) {
      confData.countries = [OPTIONS.allCountries]
    }

    setConf((data: any) => ({ ...data, countries: confData.countries }))

    if (confData?.countries?.length > 0) {
      if (showSite(confData)) {
        const allAreSelected =
          confData?.countries?.length === 1 && confData?.countries[0]?.fsiId === OPTIONS.allCountries?.fsiId
        const countryIdsLoaded = chain(confData.sites).pluck('parentId').unique().value()
        const countryIdsNotLoaded = chain(allAreSelected ? optsData.countries : confData.countries)
          .without(OPTIONS.allCountries)
          .pluck('fsiId')
          .difference(countryIdsLoaded)
          .value()
        const dataArray = await Promise.all(
          countryIdsNotLoaded.map((fsiId: any) => {
            return getSelectionsForPackage(MasterDataType.SITE, fsiId, pckgData.filePlanId)
          })
        )

        optsData.sites = optsData.sites ? optsData.sites : []

        dataArray.forEach((sites, index) => {
          sites.forEach((site: any) => {
            site.parentId = countryIdsNotLoaded[index]
            optsData.sites.push(site)
          })
        })

        // ensure uniqueness
        optsData.sites = reduce(
          optsData.sites,
          (sites: SelectOption[], site: SelectOption) => {
            const siteFound = findWhere(sites, { fsiId: site.fsiId })
            if (!siteFound) {
              sites.push(site)
            }
            return sites
          },
          []
        )

        if (optsData?.sites?.length === 1) {
          confData.sites = [optsData.sites[0]]
          setConf((data: any) => ({ ...data, sites: confData.sites }))
          setTimeout(() => {
            formRef.current.setFieldValue('sites', [confData.sites[0].fsiId])
          }, 0)
        }

        setOpts((data: any) => ({ ...data, sites: optsData.sites }))

        await onSiteChange(optsData, confData)
      }
    }
  }

  const onSiteChange = async (optsData = cloneDeep(opts), confData = cloneDeep(conf)) => {
    // ensure options are valid
    optsData.sites = reduce(
      optsData.sites,
      (sites: SelectOption[], site: SelectOption) => {
        const parentFound = findWhere(confData.countries, { fsiId: site.parentId })
        if (
          (parentFound ||
            (confData.countries.length === 1 && confData?.countries[0]?.fsiName === OPTIONS.allCountries.fsiName)) &&
          site.fsiId !== 'ALL' &&
          site !== OPTIONS.allSites
        ) {
          sites.push(site)
        }
        return sites
      },
      []
    )

    if (optsData.sites.length > 1) {
      optsData.sites.push(OPTIONS.allSites)
    }

    setOpts((data: any) => ({ ...data, sites: optsData.sites }))

    confData.sites = reduce(
      confData.sites,
      (sites: SelectOption[], site: SelectOption) => {
        const siteFound = findWhere(optsData.sites, { fsiId: site.fsiId })
        if (siteFound) {
          sites.push(siteFound)
        }
        return sites
      },
      []
    )

    if (contains(confData.sites, OPTIONS.allSites)) {
      confData.sites = [OPTIONS.allSites]
    }

    setConf((data: any) => ({ ...data, sites: confData.sites }))
  }

  return (
    <div className="data-form">
      <Formik
        initialValues={formValues}
        innerRef={formRef}
        enableReinitialize
        onSubmit={() => {}}
        validate={(values) => {
          const errors: Record<string, string> = {}
          if (!values?.product) {
            errors.product = 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
          }
          if (!!showStudy(conf) && !values?.trial) {
            errors.trial = 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
          }
          if (!!showEnvironment(conf) && !values?.environment) {
            errors.environment = 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
          }
          if (!!showCountry(conf) && !values?.countries?.length) {
            errors.countries = 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
          }
          if (!!showSite(conf) && !values?.sites?.length) {
            errors.sites = 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
          }
          $rootScope.$emit('levelValidation', Object.keys(errors).length ? false : true)
          return errors
        }}
        validateOnChange={true}
      >
        {({ values, setFieldValue, errors }) => (
          <>
            <div className={`form-group mb18`}>
              <label className="control-label">{translate('EMTF_MD_PRODUCT')}</label>
              <Select
                value={conf?.product?.fsiId}
                name="product"
                placeholder={translate('SELECT_PRODUCT')}
                onChange={(option: any) => {
                  const confData = cloneDeep(conf)
                  confData.product = option
                  setFieldValue('product', option?.value)
                  confData.trial = null
                  setFieldValue('trial', null)
                  confData.environment = null
                  setFieldValue('environment', null)
                  confData.countries = []
                  setFieldValue('countries', [])
                  confData.sites = []
                  setFieldValue('sites', [])
                  setConf((data: any) => ({ ...data, product: confData.product }))
                  onProductChange(opts, confData)
                }}
                options={transformToSelectOptions(opts?.products, 'fsiId', 'fsiName', null, true)}
                searchable
                isClearable={false}
              />
              {isSubmitted && errors?.product && <div className="error fs12">{translate(errors?.product)}</div>}
            </div>
            {!!showStudy(conf) && (
              <div className={`form-group mb18`}>
                <label className="control-label">{translate('EMTF_MD_TRIAL')}</label>
                <Select
                  value={conf?.trial?.fsiId}
                  name="trial"
                  placeholder={translate('SELECT_STUDY')}
                  onChange={(option: any) => {
                    const confData = cloneDeep(conf)
                    confData.trial = option
                    setFieldValue('trial', option?.value)
                    confData.environment = null
                    setFieldValue('environment', null)
                    confData.countries = []
                    setFieldValue('countries', [])
                    confData.sites = []
                    setFieldValue('sites', [])
                    setConf((data: any) => ({ ...data, trial: confData.trial }))
                    onStudyChange(opts, confData)
                  }}
                  options={transformToSelectOptions(opts?.trials, 'fsiId', 'fsiName', null, true)}
                  searchable
                  isClearable={false}
                />
                {isSubmitted && errors?.trial && <div className="error fs12">{translate(errors?.trial)}</div>}
              </div>
            )}
            {!!showEnvironment(conf) && (
              <div className={`form-group mb18`}>
                <label className="control-label">{translate('EMTF_MD_ENVIRONMENT')}</label>
                <Select
                  value={conf?.environment?.fsiId}
                  name="environment"
                  placeholder={translate('SELECT_ENVIRONMENT')}
                  onChange={(option: any) => {
                    const confData = cloneDeep(conf)
                    confData.environment = option
                    confData.countries = []
                    confData.sites = []
                    setTimeout(() => {
                      setFieldValue('environment', option?.value)
                      setFieldValue('countries', [])
                      setFieldValue('sites', [])
                    })
                    setConf((data: any) => ({ ...data, environment: confData.environment }))
                    onEnvironmentChange(opts, confData)
                  }}
                  options={transformToSelectOptions(opts?.environments, 'fsiId', 'fsiName', null, true)}
                  searchable
                  isClearable={false}
                />
                {isSubmitted && errors?.environment && (
                  <div className="error fs12">{translate(errors?.environment)}</div>
                )}
              </div>
            )}
            {!!showCountry(conf) && (
              <div className={`form-group mb18`}>
                <label className="control-label">{translate('EMTF_MD_COUNTRY(IES)')}</label>
                <Select
                  value={conf?.countries?.length ? pluck(conf.countries, 'fsiId') : []}
                  name="countries"
                  placeholder={translate('ETMF_ADD_MD_PLACEHOLDER_COUNTRY')}
                  onChange={(options: any) => {
                    const confData = cloneDeep(conf)
                    const optsData = cloneDeep(opts)
                    if (
                      options?.length > 1 &&
                      options[0]?.fsiId === OPTIONS.allCountries?.fsiId &&
                      options[1]?.fsiId !== OPTIONS.allCountries?.fsiId
                    ) {
                      confData.countries = [options[1]]
                      setTimeout(() => {
                        setFieldValue('countries', [options[1]?.value])
                      })
                    } else if (options[0]?.fsiId === OPTIONS.allCountries?.fsiId && options.length === 1) {
                      confData.countries = options
                      setTimeout(() => {
                        setFieldValue('countries', [options[0]?.value])
                      })
                    } else if (options[options?.length - 1]?.fsiId === OPTIONS.allCountries?.fsiId) {
                      confData.countries = [options[options?.length - 1]]
                      setTimeout(() => {
                        setFieldValue('countries', [options[options?.length - 1]?.value])
                      })
                    } else {
                      confData.countries = options
                      setTimeout(() => {
                        setFieldValue('countries', pluck(options, 'value'))
                      })
                    }
                    setFieldValue('sites', [])
                    confData.sites = []
                    optsData.sites = []
                    setConf((data: any) => ({ ...data, countries: confData.countries }))
                    setOpts((data: any) => ({ ...data, sites: optsData.sites }))
                    onCountryChange(optsData, confData)
                  }}
                  options={transformToSelectOptions(opts.countries, 'fsiId', 'fsiName', null, true)}
                  searchable
                  multi
                  isClearable={false}
                />
                {isSubmitted && errors?.countries && (
                  <div className="error fs12">{translate(errors?.countries as string)}</div>
                )}
              </div>
            )}
            {!!showSite(conf) && (
              <div className={`form-group mb18`}>
                <label className="control-label">{translate('EMTF_MD_SITE(S)')}</label>
                <Select
                  value={conf?.sites?.length ? pluck(conf.sites, 'fsiId') : []}
                  name="sites"
                  placeholder={translate('SELECT_SITE')}
                  onChange={(options: any) => {
                    const confData = cloneDeep(conf)
                    if (
                      options?.length > 1 &&
                      options[0]?.fsiId === OPTIONS.allSites?.fsiId &&
                      options[1]?.fsiId !== OPTIONS.allSites?.fsiId
                    ) {
                      confData.sites = [options[1]]
                      setFieldValue('sites', [options[1]?.value])
                    } else if (options[0]?.fsiId === OPTIONS.allSites?.fsiId && options.length === 1) {
                      confData.sites = options
                      setFieldValue('sites', [options[0]?.value])
                    } else if (options[options?.length - 1]?.fsiId === OPTIONS.allSites?.fsiId) {
                      confData.sites = [options[options?.length - 1]]
                      setFieldValue('sites', [options[options?.length - 1]?.value])
                    } else {
                      confData.sites = options
                      setFieldValue('sites', pluck(options, 'value'))
                    }
                    setConf((data: any) => ({ ...data, sites: confData.sites }))
                    onSiteChange(opts, confData)
                  }}
                  options={transformToSelectOptions(opts.sites, 'fsiId', 'fsiName', null, true)}
                  searchable
                  multi
                  isClearable={false}
                />
                {isSubmitted && errors?.sites && <div className="error fs12">{translate(errors?.sites as string)}</div>}
              </div>
            )}
          </>
        )}
      </Formik>
    </div>
  )
}

export default Level
